import { supabase } from '../lib/supabase';

// Helper: Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper: Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper: Upload file to storage
const uploadFile = async (file, userId, folder) => {
  if (!file) return null;
  
  const fileExt = file?.name?.split('.')?.pop();
  const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
  const filePath = `${userId}/${folder}/${fileName}`;
  
  const { error } = await supabase?.storage?.from('performance-media')?.upload(filePath, file, {
      cacheControl: '3600',
      upsert: false
    });
  
  if (error) throw error;
  return filePath;
};

// Helper: Get signed URL for private file
const getSignedUrl = async (filePath) => {
  if (!filePath) return null;
  
  const { data, error } = await supabase?.storage?.from('performance-media')?.createSignedUrl(filePath, 3600); // 1 hour expiry
  
  if (error) throw error;
  return data?.signedUrl;
};

// Helper: Delete file from storage
const deleteFile = async (filePath) => {
  if (!filePath) return;
  
  const { error } = await supabase?.storage?.from('performance-media')?.remove([filePath]);
  
  if (error) console.error('File deletion error:', error);
};

// ============================================
// PLAYER OPERATIONS
// ============================================

export const playerService = {
  // Create player record
  async create(playerData, files = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');
      
      // Upload files if provided
      let playerPhoto = files?.playerPhoto ? await uploadFile(files?.playerPhoto, user?.id, 'players/photos') : null;
      let matchFootage = files?.matchFootage ? await uploadFile(files?.matchFootage, user?.id, 'players/footage') : null;
      let medicalReports = files?.medicalReports ? await uploadFile(files?.medicalReports, user?.id, 'players/reports') : null;
      
      // Convert to snake_case and add user_id
      const dbData = toSnakeCase({
        ...playerData,
        userId: user?.id,
        playerPhoto,
        matchFootage,
        medicalReports
      });
      
      const { data, error } = await supabase?.from('players')?.insert(dbData)?.select()?.single();
      
      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Create player error:', error);
      return { data: null, error };
    }
  },
  
  // Get all players
  async getAll() {
    try {
      const { data, error } = await supabase?.from('players')?.select('*')?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Get signed URLs for photos
      const playersWithUrls = await Promise.all(
        (data || [])?.map(async (player) => {
          const camelPlayer = toCamelCase(player);
          if (camelPlayer?.playerPhoto) {
            camelPlayer.playerPhotoUrl = await getSignedUrl(camelPlayer?.playerPhoto);
          }
          return camelPlayer;
        })
      );
      
      return { data: playersWithUrls, error: null };
    } catch (error) {
      console.error('Get players error:', error);
      return { data: [], error };
    }
  },
  
  // Get player by ID
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('players')?.select('*')?.eq('id', id)?.single();
      
      if (error) throw error;
      
      const camelData = toCamelCase(data);
      
      // Get signed URLs for all media
      if (camelData?.playerPhoto) {
        camelData.playerPhotoUrl = await getSignedUrl(camelData?.playerPhoto);
      }
      if (camelData?.matchFootage) {
        camelData.matchFootageUrl = await getSignedUrl(camelData?.matchFootage);
      }
      if (camelData?.medicalReports) {
        camelData.medicalReportsUrl = await getSignedUrl(camelData?.medicalReports);
      }
      
      return { data: camelData, error: null };
    } catch (error) {
      console.error('Get player error:', error);
      return { data: null, error };
    }
  },
  
  // Update player record
  async update(id, playerData, files = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');
      
      // Get existing record to delete old files if new ones uploaded
      const { data: existing } = await supabase?.from('players')?.select('player_photo, match_footage, medical_reports')?.eq('id', id)?.single();
      
      // Upload new files if provided
      let playerPhoto = playerData?.playerPhoto;
      let matchFootage = playerData?.matchFootage;
      let medicalReports = playerData?.medicalReports;
      
      if (files?.playerPhoto) {
        if (existing?.player_photo) await deleteFile(existing?.player_photo);
        playerPhoto = await uploadFile(files?.playerPhoto, user?.id, 'players/photos');
      }
      if (files?.matchFootage) {
        if (existing?.match_footage) await deleteFile(existing?.match_footage);
        matchFootage = await uploadFile(files?.matchFootage, user?.id, 'players/footage');
      }
      if (files?.medicalReports) {
        if (existing?.medical_reports) await deleteFile(existing?.medical_reports);
        medicalReports = await uploadFile(files?.medicalReports, user?.id, 'players/reports');
      }
      
      // Convert to snake_case
      const dbData = toSnakeCase({
        ...playerData,
        playerPhoto,
        matchFootage,
        medicalReports
      });
      
      const { data, error } = await supabase?.from('players')?.update(dbData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Update player error:', error);
      return { data: null, error };
    }
  },
  
  // Delete player record
  async delete(id) {
    try {
      // Get file paths before deletion
      const { data: player } = await supabase?.from('players')?.select('player_photo, match_footage, medical_reports')?.eq('id', id)?.single();
      
      const { error } = await supabase?.from('players')?.delete()?.eq('id', id);
      
      if (error) throw error;
      
      // Delete associated files
      if (player?.player_photo) await deleteFile(player?.player_photo);
      if (player?.match_footage) await deleteFile(player?.match_footage);
      if (player?.medical_reports) await deleteFile(player?.medical_reports);
      
      return { error: null };
    } catch (error) {
      console.error('Delete player error:', error);
      return { error };
    }
  }
};

// ============================================
// COACH OPERATIONS
// ============================================

export const coachService = {
  // Create coach record
  async create(coachData, files = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');
      
      // Upload files if provided
      let coachPhoto = files?.coachPhoto ? await uploadFile(files?.coachPhoto, user?.id, 'coaches/photos') : null;
      let certificates = files?.certificates ? await uploadFile(files?.certificates, user?.id, 'coaches/certificates') : null;
      
      // Convert to snake_case and add user_id
      const dbData = toSnakeCase({
        ...coachData,
        userId: user?.id,
        coachPhoto,
        certificates
      });
      
      const { data, error } = await supabase?.from('coaches')?.insert(dbData)?.select()?.single();
      
      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Create coach error:', error);
      return { data: null, error };
    }
  },
  
  // Get all coaches
  async getAll() {
    try {
      const { data, error } = await supabase?.from('coaches')?.select('*')?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Get signed URLs for photos
      const coachesWithUrls = await Promise.all(
        (data || [])?.map(async (coach) => {
          const camelCoach = toCamelCase(coach);
          if (camelCoach?.coachPhoto) {
            camelCoach.coachPhotoUrl = await getSignedUrl(camelCoach?.coachPhoto);
          }
          return camelCoach;
        })
      );
      
      return { data: coachesWithUrls, error: null };
    } catch (error) {
      console.error('Get coaches error:', error);
      return { data: [], error };
    }
  },
  
  // Get coach by ID
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('coaches')?.select('*')?.eq('id', id)?.single();
      
      if (error) throw error;
      
      const camelData = toCamelCase(data);
      
      // Get signed URLs for all media
      if (camelData?.coachPhoto) {
        camelData.coachPhotoUrl = await getSignedUrl(camelData?.coachPhoto);
      }
      if (camelData?.certificates) {
        camelData.certificatesUrl = await getSignedUrl(camelData?.certificates);
      }
      
      return { data: camelData, error: null };
    } catch (error) {
      console.error('Get coach error:', error);
      return { data: null, error };
    }
  },
  
  // Update coach record
  async update(id, coachData, files = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');
      
      // Get existing record
      const { data: existing } = await supabase?.from('coaches')?.select('coach_photo, certificates')?.eq('id', id)?.single();
      
      // Upload new files if provided
      let coachPhoto = coachData?.coachPhoto;
      let certificates = coachData?.certificates;
      
      if (files?.coachPhoto) {
        if (existing?.coach_photo) await deleteFile(existing?.coach_photo);
        coachPhoto = await uploadFile(files?.coachPhoto, user?.id, 'coaches/photos');
      }
      if (files?.certificates) {
        if (existing?.certificates) await deleteFile(existing?.certificates);
        certificates = await uploadFile(files?.certificates, user?.id, 'coaches/certificates');
      }
      
      // Convert to snake_case
      const dbData = toSnakeCase({
        ...coachData,
        coachPhoto,
        certificates
      });
      
      const { data, error } = await supabase?.from('coaches')?.update(dbData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Update coach error:', error);
      return { data: null, error };
    }
  },
  
  // Delete coach record
  async delete(id) {
    try {
      // Get file paths before deletion
      const { data: coach } = await supabase?.from('coaches')?.select('coach_photo, certificates')?.eq('id', id)?.single();
      
      const { error } = await supabase?.from('coaches')?.delete()?.eq('id', id);
      
      if (error) throw error;
      
      // Delete associated files
      if (coach?.coach_photo) await deleteFile(coach?.coach_photo);
      if (coach?.certificates) await deleteFile(coach?.certificates);
      
      return { error: null };
    } catch (error) {
      console.error('Delete coach error:', error);
      return { error };
    }
  }
};

// ============================================
// GOALKEEPER OPERATIONS
// ============================================

export const goalkeeperService = {
  // Create goalkeeper record
  async create(goalkeeperData, files = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');
      
      // Upload files if provided
      let goalkeeperPhoto = files?.goalkeeperPhoto ? await uploadFile(files?.goalkeeperPhoto, user?.id, 'goalkeepers/photos') : null;
      let matchHighlights = files?.matchHighlights ? await uploadFile(files?.matchHighlights, user?.id, 'goalkeepers/highlights') : null;
      
      // Convert to snake_case and add user_id
      const dbData = toSnakeCase({
        ...goalkeeperData,
        userId: user?.id,
        goalkeeperPhoto,
        matchHighlights
      });
      
      const { data, error } = await supabase?.from('goalkeepers')?.insert(dbData)?.select()?.single();
      
      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Create goalkeeper error:', error);
      return { data: null, error };
    }
  },
  
  // Get all goalkeepers
  async getAll() {
    try {
      const { data, error } = await supabase?.from('goalkeepers')?.select('*')?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Get signed URLs for photos
      const goalkeepersWithUrls = await Promise.all(
        (data || [])?.map(async (goalkeeper) => {
          const camelGoalkeeper = toCamelCase(goalkeeper);
          if (camelGoalkeeper?.goalkeeperPhoto) {
            camelGoalkeeper.goalkeeperPhotoUrl = await getSignedUrl(camelGoalkeeper?.goalkeeperPhoto);
          }
          return camelGoalkeeper;
        })
      );
      
      return { data: goalkeepersWithUrls, error: null };
    } catch (error) {
      console.error('Get goalkeepers error:', error);
      return { data: [], error };
    }
  },
  
  // Get goalkeeper by ID
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('goalkeepers')?.select('*')?.eq('id', id)?.single();
      
      if (error) throw error;
      
      const camelData = toCamelCase(data);
      
      // Get signed URLs for all media
      if (camelData?.goalkeeperPhoto) {
        camelData.goalkeeperPhotoUrl = await getSignedUrl(camelData?.goalkeeperPhoto);
      }
      if (camelData?.matchHighlights) {
        camelData.matchHighlightsUrl = await getSignedUrl(camelData?.matchHighlights);
      }
      
      return { data: camelData, error: null };
    } catch (error) {
      console.error('Get goalkeeper error:', error);
      return { data: null, error };
    }
  },
  
  // Update goalkeeper record
  async update(id, goalkeeperData, files = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');
      
      // Get existing record
      const { data: existing } = await supabase?.from('goalkeepers')?.select('goalkeeper_photo, match_highlights')?.eq('id', id)?.single();
      
      // Upload new files if provided
      let goalkeeperPhoto = goalkeeperData?.goalkeeperPhoto;
      let matchHighlights = goalkeeperData?.matchHighlights;
      
      if (files?.goalkeeperPhoto) {
        if (existing?.goalkeeper_photo) await deleteFile(existing?.goalkeeper_photo);
        goalkeeperPhoto = await uploadFile(files?.goalkeeperPhoto, user?.id, 'goalkeepers/photos');
      }
      if (files?.matchHighlights) {
        if (existing?.match_highlights) await deleteFile(existing?.match_highlights);
        matchHighlights = await uploadFile(files?.matchHighlights, user?.id, 'goalkeepers/highlights');
      }
      
      // Convert to snake_case
      const dbData = toSnakeCase({
        ...goalkeeperData,
        goalkeeperPhoto,
        matchHighlights
      });
      
      const { data, error } = await supabase?.from('goalkeepers')?.update(dbData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Update goalkeeper error:', error);
      return { data: null, error };
    }
  },
  
  // Delete goalkeeper record
  async delete(id) {
    try {
      // Get file paths before deletion
      const { data: goalkeeper } = await supabase?.from('goalkeepers')?.select('goalkeeper_photo, match_highlights')?.eq('id', id)?.single();
      
      const { error } = await supabase?.from('goalkeepers')?.delete()?.eq('id', id);
      
      if (error) throw error;
      
      // Delete associated files
      if (goalkeeper?.goalkeeper_photo) await deleteFile(goalkeeper?.goalkeeper_photo);
      if (goalkeeper?.match_highlights) await deleteFile(goalkeeper?.match_highlights);
      
      return { error: null };
    } catch (error) {
      console.error('Delete goalkeeper error:', error);
      return { error };
    }
  }
};

// ============================================
// REAL-TIME SUBSCRIPTIONS
// ============================================

export const subscribeToPerformanceRecords = (callback) => {
  const channel = supabase?.channel('performance_records_changes')?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'players'
      },
      (payload) => callback('players', payload)
    )?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'coaches'
      },
      (payload) => callback('coaches', payload)
    )?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'goalkeepers'
      },
      (payload) => callback('goalkeepers', payload)
    )?.subscribe();
  
  return channel;
};

export const unsubscribeFromPerformanceRecords = (channel) => {
  if (channel) {
    supabase?.removeChannel(channel);
  }
};